import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Link,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
  Slider,
  IconButton,
  Autocomplete,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { retailerCatalogApi } from '../../services/retailerCatalog';
import { proposedArticlesApi } from '../../services/proposed-articles';
import { auditsApi } from '../../services/audits';
import { CatalogueIcon } from './CatalogueIcon';
import { ChannelLogo } from '../../components/news/ChannelLogo';
import { CHANNEL_LABEL } from '../../components/news/channels';
import type {
  Catalogue,
  ContentChannel,
  ProductContentAngle,
  GroundedProduct,
  ContentTopic,
} from '@traaker/shared';

const ANGLES: { id: ProductContentAngle; label: string; help: string }[] = [
  {
    id: 'guide',
    label: "Guide d'achat",
    help: "Article de conseil qui aide le lecteur à CHOISIR : critères de sélection puis recommandations de produits par cas d'usage/budget. Liens vers vos pages produit uniquement.",
  },
  {
    id: 'benchmark',
    label: 'Comparatif',
    help: "Comparatif structuré (souvent un tableau) qui oppose les produits sur des critères concrets et met en avant les atouts des vôtres. Liens vers vos produits + les concurrents comparés.",
  },
  {
    id: 'story',
    label: 'Éditorial',
    help: 'Contenu narratif/storytelling autour des produits et de leurs usages réels, orienté bénéfice-client plutôt que fiche technique. Placements produit au fil du récit.',
  },
  {
    id: 'promo',
    label: 'Nouveautés / promo',
    help: 'Met en avant nouveautés et offres, avec urgence et bénéfice immédiat, ton commercial mais crédible. CTA clair vers les produits.',
  },
];

const CHANNELS: ContentChannel[] = ['blog_seo', 'linkedin_post', 'x_thread', 'instagram', 'newsletter'];

/**
 * Product content generator (Phase 6) — the Studio's `catalog` source mode, as a
 * focused page: pick catalogue(s) + an editorial angle + a free-text brief →
 * resolve the closest real products (vector search) → generate a product-grounded
 * article via the existing news_watch pipeline. content_idea recommendations from
 * the competitive analysis (Phase 5) show up as one-click topic suggestions.
 */
export default function ProductContent() {
  const { activeBrand } = useBrand();
  const { show } = useToast();
  const navigate = useNavigate();
  const brandId = activeBrand?.brandId;

  const [catalogues, setCatalogues] = useState<Catalogue[]>([]);
  const [catalogueIds, setCatalogueIds] = useState<string[]>([]);
  const [angle, setAngle] = useState<ProductContentAngle>('guide');
  const [scope, setScope] = useState('');
  const [channels, setChannels] = useState<ContentChannel[]>(['blog_seo', 'linkedin_post']);
  // Item k: how many grounded products to include, and the chosen ones (the
  // user can remove individual products from the preview before generating).
  const [groundCount, setGroundCount] = useState(6);
  const [grounded, setGrounded] = useState<GroundedProduct[] | null>(null);
  const [resolving, setResolving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [topics, setTopics] = useState<ContentTopic[]>([]);
  // The competitive-analysis idea the brief was pre-filled from (if any) — removed
  // from the suggestions once a content is generated from it.
  const [selectedTopicId, setSelectedTopicId] = useState('');

  const loadTopics = useCallback(() => {
    if (!brandId) return;
    proposedArticlesApi.listContentTopics(brandId).then(setTopics).catch(() => setTopics([]));
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return;
    retailerCatalogApi.list(brandId).then((cs) => {
      setCatalogues(cs);
      const firstOptimized = cs.find((c) => c.lastOptimizedAt);
      if (firstOptimized) setCatalogueIds([firstOptimized.catalogueId]);
    });
    loadTopics();
  }, [brandId, loadTopics]);

  if (!brandId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Sélectionnez une marque.</Alert>
      </Box>
    );
  }

  const toggleChannel = (id: ContentChannel) =>
    setChannels((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Selected catalogues' descriptions — shown as context + threaded to the generator.
  const selectedDescriptions = useMemo(
    () =>
      catalogues
        .filter((c) => catalogueIds.includes(c.catalogueId) && c.description)
        .map((c) => `${c.name} : ${c.description}`),
    [catalogues, catalogueIds],
  );

  // Resolve grounded products: vector-search each selected catalogue for the brief,
  // merge + dedup, keep the top matches. Uses the existing search endpoint.
  const resolveProducts = async () => {
    if (!scope.trim() || catalogueIds.length === 0) return;
    setResolving(true);
    try {
      const perCat = await Promise.all(
        // No type filter → products AND category pages (both ground the article).
        // hydrate=true → friendly displayName for the link anchor (raw catalogue
        // names are often unusable, e.g. "AAAA Plancha - LIVOO - DOC292 - Inox").
        catalogueIds.map((id) =>
          retailerCatalogApi.search(brandId, id, scope, undefined, 8, true).catch(() => []),
        ),
      );
      const seen = new Set<string>();
      const merged: GroundedProduct[] = [];
      for (const hit of perCat.flat().sort((a, b) => (b.score || 0) - (a.score || 0))) {
        if (seen.has(hit.productId)) continue;
        seen.add(hit.productId);
        merged.push({
          productId: hit.productId,
          name: hit.name,
          url: hit.url,
          ...(hit.displayName ? { displayName: hit.displayName } : {}),
        });
        // Recall stays wide (8/catalogue above); slice to the user's chosen
        // count (item k) so what's previewed is exactly what generates.
        if (merged.length >= groundCount) break;
      }
      setGrounded(merged);
      if (!merged.length) show('Aucun produit proche trouvé pour ce périmètre.', { severity: 'warning' });
    } catch (e: any) {
      show(e?.message || 'Recherche impossible', { severity: 'error' });
    } finally {
      setResolving(false);
    }
  };

  const generate = async () => {
    if (!scope.trim()) {
      show('Décrivez le contenu à créer.', { severity: 'warning' });
      return;
    }
    setLaunching(true);
    try {
      await auditsApi.create(
        'news_watch',
        {
          contentCreation: {
            sourceMode: 'catalog',
            seed: scope.trim(),
            catalogueIds,
            catalogContext: selectedDescriptions.join(' · ') || undefined,
            productAngle: angle,
            groundedProducts: grounded || undefined,
            format: 'standard',
            channels,
            patterns: angle === 'benchmark' ? ['listicle'] : angle === 'guide' ? ['guide'] : [],
            objective: angle === 'promo' ? 'conversion' : 'acquisition',
          },
        } as any,
        brandId,
      );
      // A used idea is single-shot — drop it from the suggestions. Awaited
      // (item j) so a delete failure doesn't race the navigate; the list
      // reloads on next mount regardless, so a failure here just leaves the
      // idea visible again rather than silently stranding it.
      if (selectedTopicId) {
        try {
          await proposedArticlesApi.deleteContentTopic(selectedTopicId, brandId);
          setTopics((prev) => prev.filter((t) => t.topicId !== selectedTopicId));
        } catch {
          /* re-fetched on next mount anyway */
        }
        setSelectedTopicId('');
      }
      show('Génération lancée — retrouvez le contenu dans le Studio.', { severity: 'success' });
      // Pass the launched channels so the Studio shows the "en création" skeleton
      // card (same as clicking Générer directly on /news-watch).
      navigate('/news-watch', { state: { pendingChannels: channels } });
    } catch (e: any) {
      if (e?.body?.code === 'QUOTA_EXCEEDED') {
        show('Quota de créations atteint ce mois-ci.', { severity: 'warning' });
      } else {
        show(e?.message || 'Génération impossible', { severity: 'error' });
      }
    } finally {
      setLaunching(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Création de contenu produit
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Point de départ pour créer un contenu (guide, comparatif, éditorial, promo) ancré sur de
        vrais produits de votre catalogue. Une fois généré, il rejoint le{' '}
        <Link component="button" type="button" onClick={() => navigate('/news-watch')} sx={{ verticalAlign: 'baseline' }}>
          Studio de création
        </Link>{' '}
        — où se trouvent tous vos contenus (édition, publication, historique).
      </Typography>

      {catalogues.length === 0 ? (
        <Alert severity="info">Créez d'abord un catalogue dans l'onglet Catalogue.</Alert>
      ) : (
        <Stack spacing={2.5}>
          {/* content_idea suggestions from the competitive analysis — pick one to
              pre-fill the brief (and remember which topic it came from, so it can
              be marked used after generation). */}
          {topics.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Idées issues de l'analyse concurrentielle
              </Typography>
              <Autocomplete
                size="small"
                options={topics}
                getOptionLabel={(t) => t.topic}
                value={topics.find((t) => t.topicId === selectedTopicId) || null}
                onChange={(_, t) => {
                  setSelectedTopicId(t?.topicId || '');
                  if (t) setScope(t.topic);
                }}
                renderOption={(props, t) => (
                  <li {...props} key={t.topicId}>
                    <Stack direction="row" alignItems="center" sx={{ width: '100%' }} spacing={1}>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                        {t.topic}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Supprimer cette idée"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await proposedArticlesApi.deleteContentTopic(t.topicId, brandId);
                            setTopics((prev) => prev.filter((x) => x.topicId !== t.topicId));
                            if (selectedTopicId === t.topicId) setSelectedTopicId('');
                            show('Idée supprimée', { severity: 'success' });
                          } catch (err: any) {
                            show(err?.message || 'Suppression impossible', { severity: 'error' });
                          }
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Choisir une idée à pré-remplir…" />
                )}
              />
            </Box>
          )}

          <Box>
            <Typography data-tour="product-content-sources" variant="subtitle2" sx={{ mb: 0.5 }}>
              Catalogue(s) source
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                displayEmpty
                value={catalogueIds}
                onChange={(e) =>
                  setCatalogueIds(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                  )
                }
                renderValue={(ids) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {ids.map((id) => {
                      const c = catalogues.find((x) => x.catalogueId === id);
                      return (
                        <Chip
                          key={id}
                          size="small"
                          icon={<CatalogueIcon icon={c?.icon} sx={{ fontSize: 14 }} />}
                          label={c?.name || id}
                        />
                      );
                    })}
                  </Stack>
                )}
              >
                {catalogues.map((c) => {
                  const notOptimized = !c.lastOptimizedAt;
                  return (
                    <MenuItem key={c.catalogueId} value={c.catalogueId} disabled={notOptimized}>
                      <CatalogueIcon icon={c.icon} sx={{ fontSize: 18, mr: 1 }} />
                      {c.name}
                      {notOptimized && ' — non optimisé'}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {catalogues.some((c) => !c.lastOptimizedAt) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Un catalogue "non optimisé" doit d'abord être analysé (onglet Catalogue) — sans cela,
                les noms/SKU produits ne sont pas correctement extraits et le contenu généré sera peu fiable.
              </Typography>
            )}
            {selectedDescriptions.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {selectedDescriptions.join(' · ')}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Produits à inclure : {groundCount}
            </Typography>
            <Slider
              value={groundCount}
              onChange={(_, v) => setGroundCount(v as number)}
              min={1}
              max={12}
              step={1}
              size="small"
              valueLabelDisplay="auto"
              sx={{ maxWidth: 300 }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Type de contenu
            </Typography>
            <ToggleButtonGroup
              value={angle}
              exclusive
              size="small"
              onChange={(_, v) => v && setAngle(v)}
            >
              {ANGLES.map((a) => (
                <ToggleButton key={a.id} value={a.id} sx={{ textTransform: 'none', gap: 0.5 }}>
                  {a.label}
                  <Tooltip title={a.help} arrow>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 15, opacity: 0.6 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <TextField
            label="Que doit couvrir ce contenu ?"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            placeholder="ex. chaussures de running pour débutants, budget < 100€, confort et amorti"
            helperText="Périmètre libre — on retrouve les produits les plus proches dans votre catalogue."
          />

          <Box>
            <Button
              variant="outlined"
              startIcon={resolving ? <CircularProgress size={16} /> : <SearchIcon />}
              onClick={resolveProducts}
              disabled={resolving || !scope.trim() || catalogueIds.length === 0}
            >
              Trouver les produits à mettre en avant
            </Button>
            {grounded && grounded.length > 0 && (
              <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {grounded.length} produit(s) — le contenu s'appuiera sur ceux-ci :
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {grounded.map((p) => (
                    <Stack key={p.productId} direction="row" alignItems="center" spacing={0.5}>
                      <Link
                        href={p.url.startsWith('http') ? p.url : `https://${p.url}`}
                        target="_blank"
                        rel="noopener"
                        variant="caption"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, flex: 1 }}
                      >
                        {p.displayName || p.name || p.url}
                        <OpenInNewIcon sx={{ fontSize: 12 }} />
                      </Link>
                      <IconButton
                        size="small"
                        aria-label="Retirer ce produit"
                        onClick={() =>
                          setGrounded((prev) => (prev || []).filter((x) => x.productId !== p.productId))
                        }
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Canaux
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {CHANNELS.map((c) => {
                const selected = channels.includes(c);
                return (
                  <Chip
                    key={c}
                    icon={
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 16,
                          height: 16,
                          borderRadius: '3px',
                          // White tile keeps brand-colored logos legible on the
                          // filled (selected) primary background.
                          bgcolor: selected ? '#fff' : 'transparent',
                          p: selected ? '1px' : 0,
                        }}
                      >
                        <ChannelLogo channel={c} size={14} />
                      </Box>
                    }
                    label={CHANNEL_LABEL[c]}
                    size="small"
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggleChannel(c)}
                  />
                );
              })}
            </Stack>
          </Box>

          <Box>
            <Button
              variant="contained"
              size="large"
              startIcon={launching ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
              onClick={generate}
              disabled={launching || !scope.trim() || channels.length === 0}
            >
              Générer le contenu
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Le contenu apparaît dans le Studio de création (consomme une création mensuelle).
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
